/**
 * Filter keydown events to only allow numeric characters.
 * The field should be a `<input type="number">`.
 * @func
 * @param  {KeyboardEvent} event The keydown event
 */

import Joi from 'joi'

import i18n from '@hapana/shared/web/i18n/index'

import { regexToAllowedChars } from './string.util'

export const allowOnlyNumbers = (event) => {
  const { key } = event
  if (key.length > 1) return // Allow control keys: Backspace, Delete, ArrowLeft, etc.
  if (!/^[0-9.]$/.test(key)) {
    event.preventDefault()
  }
}

// This happens in case name is a string that contains a nested object reference.
// It usually happens inside a fieldArray.
export const getNestedFormError = (errors, name) => {
  const nameArray = name.split('.')
  if (nameArray.length > 1) {
    let error = errors

    for (let i = 0; i < nameArray.length; i++) {
      if (isNaN(nameArray[i])) {
        error = error[nameArray[i]]
      } else {
        error = error[parseInt(nameArray[i])]
      }

      if (error === undefined || error === null) {
        error = null
        break
      }
    }
    return error?.message
  }
}

export const generateFormFields = (fields) => {
  const schemaObject = {}
  Object.values(fields).forEach((field) => {
    schemaObject[field.name] = field.validations?.isRequired
      ? Joi.string().required()
      : Joi.string().allow('')
    schemaObject[field.name] = field.validations?.max
      ? schemaObject[field.name].max(field.validations?.max)
      : schemaObject[field.name]
    schemaObject[field.name] = field.validations?.pattern
      ? schemaObject[field.name].pattern(field.validations?.pattern.base)
      : schemaObject[field.name]
    schemaObject[field.name] = schemaObject[field.name].messages({
      ...(field.validations?.isRequired ? { 'string.empty': i18n.t('errors:forms.required') } : {}),
      ...(field.validations?.max
        ? {
            'string.max': i18n.t('errors:forms.maxLength', {
              count: field.validations?.max,
            }),
          }
        : {}),
      ...(field.validations?.pattern
        ? {
            'string.pattern.base': field.validations?.pattern.message,
          }
        : {}),
    })
  })

  return Joi.object(schemaObject)
}

/**
 * Build a Joi schema for a single field based on the config.
 * For complex fields, you can supply a `customSchema` function in the field config.
 */
export function buildFieldSchema(fieldConfig, fieldsMap) {
  // If the field config has a customSchema, just defer to it
  if (typeof fieldConfig.customSchema === 'function') {
    return fieldConfig.customSchema(Joi, i18n, fieldsMap)
  }
  const { type = 'string', validations = {} } = fieldConfig
  let schema
  // Base schema depending on type
  switch (type) {
    case 'boolean':
      schema = Joi.boolean()
      break
    case 'number':
      schema = Joi.number()
      break
    case 'array':
      schema = Joi.array()
      if (fieldConfig.items?.type === 'object') {
        schema = schema.items(
          Joi.object(
            Object.entries(fieldConfig.items.fields).reduce((itemSchema, [itemKey, itemConfig]) => {
              itemSchema[itemKey] = buildFieldSchema(itemConfig, fieldsMap)
              return itemSchema
            }, {})
          ).unknown(true)
        )
      }
      break
    case 'object':
      schema = Joi.object(
        Object.entries(fieldConfig.fields).reduce((objSchema, [objKey, objConfig]) => {
          objSchema[objKey] = buildFieldSchema(objConfig, fieldsMap)
          return objSchema
        }, {})
      ).unknown(true)
      break
    case 'string':
    default:
      schema = Joi.string()
      break
  }
  if (validations.required) {
    schema = schema.required()
  }
  if (validations.pattern && type === 'string') {
    schema = schema.pattern(validations.pattern)
  }
  if (validations.max && (type === 'string' || type === 'array')) {
    schema = schema.max(validations.max)
  }
  if (validations.empty) {
    schema = schema.allow('')
  }
  const messages = {}
  // Pattern error message
  if (validations.pattern && type === 'string') {
    const allowedChars = regexToAllowedChars(validations.pattern)
    messages['string.pattern.base'] = i18n.t('errors:forms.specialCharacters', {
      charList: allowedChars.string,
      count: allowedChars.length,
    })
  }
  // Max length error message
  if (validations.max && type === 'string') {
    messages['string.max'] = i18n.t('errors:forms.maxLength', {
      count: validations.max,
    })
  }
  // Required error message
  if (validations.required && type === 'string') {
    messages['string.empty'] = i18n.t('errors:forms.required')
  } else if (validations.required && type === 'number') {
    messages['number.empty'] = i18n.t('errors:forms.required')
  }
  schema = schema.messages(messages)
  return schema
}
